import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import SuicideDetailForm from "./SuicideDetailForm";

import { createSuicideDetail } from "../../../../store/slices/suicideDetailsSlice";
import { fetchPatient } from "../../../../store/slices/patientsSlice";
import { getApiErrorMessage } from "../../../../utils/apiError";

// ==================== Helper: camelCase → snake_case ====================
const toSnakeCasePayload = (data) => ({
    case_id: data.caseId,
    patient_id: data.patientId,
    client_name: data.clientName,
    completed_by: data.completedBy,
    date: data.date,

    q1: data.q1,
    q2: data.q2,
    q3: data.q3,
    q4: data.q4,

    q5: data.q5,
    q5_attempts: data.q5_attempts,
    q5_last_attempt: data.q5_lastAttempt,
    q5_changes: data.q5_changes,

    q6: data.q6,
    q6_events: data.q6_events,
    q6_other: data.q6_other,

    q7: data.q7,
    q7_frequency: data.q7_frequency,
    q7_duration: data.q7_duration,
    q7_intensity_severe: data.q7_intensitySevere,
    q7_intensity_week: data.q7_intensityWeek,

    q8: data.q8,
    q8_method: data.q8_method,
    q8_access: data.q8_access,
    q8_location: data.q8_location,
    q8_preparations: data.q8_preparations,
    q8_likelihood: data.q8_likelihood,

    q9_stopped: data.q9_stopped,

    q10: data.q10,
    q10_people: data.q10_people,
    q10_contact: data.q10_contact,

    q11_past_help: data.q11_pastHelp,

    presentation: data.presentation,
    presentation_other: data.presentation_other,

    worker_risk_level: data.workerRiskLevel,
});

const AddSuicideDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();

    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        caseId: caseId || null,
        patientId: patientId || null,

        clientName: "",
        completedBy: "",
        date: "",

        q1: "", q2: "", q3: "", q4: "",
        q5: "", q5_attempts: "", q5_lastAttempt: "", q5_changes: "",
        q6: "", q6_events: [], q6_other: "",
        q7: "", q7_frequency: "", q7_duration: "",
        q7_intensitySevere: "", q7_intensityWeek: "",
        q8: "", q8_method: "", q8_access: "", q8_location: "",
        q8_preparations: "", q8_likelihood: "",
        q9_stopped: "",
        q10: "", q10_people: "", q10_contact: "",
        q11_pastHelp: "",
        presentation: [],
        presentation_other: "",
        workerRiskLevel: "",
    });

    useEffect(() => {
        if (patientId) {
            dispatch(fetchPatient(patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        setFormData((prev) => ({
                            ...prev,
                            clientName: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                        }));
                    }
                })
                .catch((err) => console.error("Failed to prefill patient name for Suicide Detail:", err));
        }
    }, [dispatch, patientId]);

    // ==================== Change Handler ====================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            if (name === "q6_events" || name === "presentation") {
                setFormData((prev) => ({
                    ...prev,
                    [name]: checked
                        ? [...prev[name], value]
                        : prev[name].filter((v) => v !== value),
                }));
            }
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    // ==================== Submit ====================
    // ==================== Submit ====================
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // Create a copy of formData to modify
            const formDataToSend = { ...formData };

            // Helper function to check if answer is "no"
            const isNo = (value) => value?.toLowerCase() === "no";

            // Q5 logic
            if (isNo(formDataToSend.q5)) {
                formDataToSend.q5_attempts = null;
                formDataToSend.q5_lastAttempt = null;
                formDataToSend.q5_changes = null;
            }

            // Q6 logic
            if (isNo(formDataToSend.q6)) {
                formDataToSend.q6_events = [];
                formDataToSend.q6_other = null;
            }

            // Q7 logic
            if (isNo(formDataToSend.q7)) {
                formDataToSend.q7_frequency = null;
                formDataToSend.q7_duration = null;
                formDataToSend.q7_intensitySevere = null;
                formDataToSend.q7_intensityWeek = null;
            }

            // Q8 logic
            if (isNo(formDataToSend.q8)) {
                formDataToSend.q8_method = null;
                formDataToSend.q8_access = null;
                formDataToSend.q8_location = null;
                formDataToSend.q8_preparations = null;
                formDataToSend.q8_likelihood = null;
            }

            // Q10 logic
            if (isNo(formDataToSend.q10)) {
                formDataToSend.q10_people = null;
                formDataToSend.q10_contact = null;
            }

            const presentationArray = formDataToSend.presentation || [];
            const hasOtherOption = presentationArray.some(item =>
                item === "Other" || item === "other"
            );

            if (!hasOtherOption) {
                formDataToSend.presentation_other = null;
            }

            const payload = toSnakeCasePayload(formDataToSend);

            const res = await dispatch(createSuicideDetail(payload)).unwrap();

            Swal.fire({
                title: "Saved!",
                text: "Suicide Risk Assessment completed.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate(`/residential/optional/suicide-detail/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (error) {
            Swal.fire(
                "Error",
                getApiErrorMessage(error, "Failed to save assessment"),
                "error"
            );
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <SuicideDetailForm
                                formData={formData}
                                handleChange={handleChange}
                                setFormData={setFormData}
                            />

                            <div className="col-12 d-flex justify-content-end mt-3">
                                <button type="submit" className="btn btn-primary px-4 sticky-form-submit">
                                    <i className="ri-save-2-line me-1"></i> Save
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddSuicideDetail;
